import { Button, IconSearch, Kbd, navItemVariants, Wordmark } from '@ccp/ui';
import { Link, useLocation } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { shortcutById, shortcutLabel } from '../../lib/keyboard/shortcut-map.js';
import type { ConnectionStatus } from '../../realtime/connection.machine.js';
import { ConnectionBanner } from '../../realtime/connection-banner.js';
import { WorkspaceAccountMenu } from './account-menu.js';
import { areaAt, type WorkspaceArea } from './areas.js';

const paletteShortcut = shortcutById('global.palette');

export interface WorkspaceShellProps {
  /** The areas this user may go to. The ones they may not are not rendered at all. */
  readonly areas: readonly WorkspaceArea[];
  readonly connection: ConnectionStatus;
  /** Who is signed in, or nothing at all on the sign-in page. */
  readonly userName?: string | undefined;
  readonly userEmail?: string | undefined;
  readonly onSignOut?: (() => void) | undefined;
  readonly onOpenCommandPalette?: (() => void) | undefined;
  /**
   * Whether the account menu offers density.
   *
   * Off before anybody has signed in: the sign-in page is one form, and a control for how tightly
   * to pack a board nobody is looking at is noise in front of the one thing that page is for.
   */
  readonly showDensity?: boolean;
  readonly children: ReactNode;
}

/**
 * The chrome every screen sits in: who you are, where you can go, and whether what you are looking
 * at is current.
 *
 * It takes everything as props and reads no session of its own, which is what lets every state it
 * can be in — degraded, resyncing, an agent's navigation, an administrator's — be a story rather
 * than a thing that only exists when a server is answering. `__root.tsx` is the half that knows the
 * session; this is the half that draws it.
 *
 * The navigation shows only the areas the user holds a permission for. That is a courtesy and not a
 * boundary: the route guard behind each link refuses the same people, and the API behind the route
 * refuses them again.
 */
export function WorkspaceShell({
  areas,
  connection,
  userName,
  userEmail,
  onSignOut,
  onOpenCommandPalette,
  showDensity = false,
  children,
}: WorkspaceShellProps) {
  const { t } = useTranslation();
  const current = areaAt(useLocation({ select: (location) => location.pathname }));

  return (
    <div className="flex min-h-full flex-col bg-(--color-background)">
      {/*
        **It wraps.** It did not, and that made every screen in the workspace scroll sideways on
        anything narrower than about 1100 pixels (M4.5-11). It is also much shorter now: the three
        preference selects that made up most of its width are in the account menu (M5.5-01), so
        on a laptop it is one row — the product, where you can go, and the two controls that are
        about you rather than the page.
      */}
      <header className="flex flex-wrap items-center gap-(--space-sm) gap-x-(--space-lg) border-b border-(--color-border) bg-(--color-surface) px-(--space-lg) py-(--space-sm) md:px-(--space-2xl)">
        <Link
          to="/"
          className="rounded-(--radius-md) outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring)"
        >
          <Wordmark name={t('product')} />
        </Link>
        {areas.length === 0 ? null : (
          <nav aria-label={t('nav.label')} className="min-w-0">
            <ul className="flex flex-wrap items-center gap-(--space-xs)">
              {areas.map((area) => (
                <li key={area.id}>
                  {/* `aria-current` is the accessible half and the styled half at once: the area
                      a screen reader announces as current is the one that looks it. */}
                  <Link
                    to={area.path}
                    aria-current={current?.id === area.id ? 'page' : undefined}
                    className={navItemVariants()}
                  >
                    {t(area.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="ms-auto flex min-w-0 items-center gap-(--space-xs)">
          {onOpenCommandPalette === undefined ? null : (
            <Button variant="ghost" size="sm" onClick={onOpenCommandPalette}>
              <IconSearch aria-hidden="true" className="size-(--size-icon-sm)" />
              {t('palette.open')}
              {paletteShortcut === undefined ? null : (
                <Kbd
                  keys={shortcutLabel(paletteShortcut).split(' + ')}
                  className="hidden md:inline-flex"
                />
              )}
            </Button>
          )}
          <WorkspaceAccountMenu
            userName={userName}
            email={userEmail}
            onSignOut={onSignOut}
            showDensity={showDensity}
          />
        </div>
      </header>
      <ConnectionBanner status={connection} />
      <main className="flex min-w-0 flex-1 flex-col">{children}</main>
    </div>
  );
}
